/**
 * Guided self-test (Phase 4 slice 2) hooks — React Query wrappers over
 * `POST/GET /api/workflows/:id/self-test*` (backend/app/routers/workflows.py).
 * Types are snake_case straight through (workflowConfig.ts convention) —
 * unlike Replay Lab's camelCase transform, there is no field mapping here.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient, ApiError } from '@/services/apiClient'
import type { SelfTestRun } from '@/types/workflowConfig'

/** Poll a run while it is executing; stop the moment it reaches a terminal
 * status. Returned to React Query's `refetchInterval`. */
export function selfTestRefetchInterval(status?: SelfTestRun['status']): number | false {
  return status === 'running' ? 2000 : false
}

/** Kick off a guided self-test run. No request body — the endpoint resolves
 * the template's declared suite + the workflow's bound persona server-side.
 * A 409 (`self_test_already_running`, carrying the in-flight `run_id`)
 * surfaces as an `ApiError` for the caller to branch on. */
export function useStartSelfTest(workflowId: string) {
  const qc = useQueryClient()
  return useMutation<SelfTestRun, Error, void>({
    mutationFn: async () =>
      apiClient.post<SelfTestRun>(`/api/workflows/${workflowId}/self-test`, {}),
    onSuccess: (run) => {
      qc.setQueryData(['workflows', 'self-test', workflowId, run.id], run)
    },
  })
}

/** Poll a single self-test run by id. Disabled while `runId` is null (no
 * run selected yet). */
export function useSelfTestRun(workflowId: string, runId: string | null) {
  return useQuery<SelfTestRun, Error>({
    queryKey: ['workflows', 'self-test', workflowId, runId],
    queryFn: () =>
      apiClient.get<SelfTestRun>(`/api/workflows/${workflowId}/self-test/runs/${runId}`),
    enabled: !!runId,
    refetchInterval: (query) => selfTestRefetchInterval(query.state.data?.status),
  })
}

/** Most recent self-test run for a workflow. 404 ("no runs yet") resolves to
 * `null` rather than an error — that is a legitimate steady state, not a
 * failure. Any other error rethrows. */
export function useLatestSelfTestRun(workflowId: string, enabled: boolean) {
  return useQuery<SelfTestRun | null, Error>({
    queryKey: ['workflows', 'self-test', workflowId, 'latest'],
    queryFn: async () => {
      try {
        return await apiClient.get<SelfTestRun>(`/api/workflows/${workflowId}/self-test/latest`)
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          return null
        }
        throw err
      }
    },
    enabled,
    refetchInterval: (query) => selfTestRefetchInterval(query.state.data?.status),
  })
}
